// src/pages/employeeCommission/components/EmployeeLevelSingleModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Info } from 'lucide-react';
import { getUsers } from '../../../services/api/users';
import { upsertCommissionConfig } from '../../../services/api/employeeCommission';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import styles from './PageModals.module.css';

const BASIS_OPTIONS = [
  { value: 'order_count',    labelKey: 'orderCount' },
  { value: 'order_quantity', labelKey: 'orderQuantity' },
  { value: 'order_value',    labelKey: 'orderValue' },
];

const TIME_RANGE_OPTIONS = [
  { value: 'monthly',     label: 'Monthly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'weekly',      label: 'Weekly' },
];

const EmployeeLevelSingleModal = ({ onClose, onSaved, existingConfig = null }) => {
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();

  const isEdit = !!existingConfig;

  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [form, setForm] = useState({
    userId:            existingConfig?.userId            || '',
    confirmCommission: existingConfig?.confirmCommission ?? '',
    createCommission:  existingConfig?.createCommission  ?? '',
    calculationBasis:  existingConfig?.calculationBasis  || 'order_count',
    startDate:         existingConfig?.startDate         || '',
    timeRange:         existingConfig?.timeRange         || 'monthly',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const searchTimer = useRef(null);

  useEffect(() => {
    loadUsers('');
  }, []);

  const loadUsers = async (search) => {
    try {
      const res = await getUsers({ limit: 50, search });
      setUsers(res?.users || []);
    } catch {
      // ignore
    }
  };

  const handleUserSearch = (val) => {
    setUserSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => loadUsers(val), 400);
  };

  const validate = () => {
    const errs = {};
    if (!form.userId)
      errs.userId = t('employeeCommission.selectEmployeeRequired');
    if (form.confirmCommission === '' || form.confirmCommission === null)
      errs.confirmCommission = t('employeeCommission.confirmCommissionRequired');
    if (form.createCommission === '' || form.createCommission === null)
      errs.createCommission = t('employeeCommission.createCommissionRequired');
    if (!form.calculationBasis)
      errs.calculationBasis = t('employeeCommission.calculationBasisRequired');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await upsertCommissionConfig({
        userId:            parseInt(form.userId),
        productId:         null,
        confirmCommission: parseFloat(form.confirmCommission),
        createCommission:  parseFloat(form.createCommission),
        calculationBasis:  form.calculationBasis,
        startDate:         form.startDate || null,
        timeRange:         form.timeRange,
      });
      success(t('employeeCommission.configSaved'));
      onSaved();
      onClose();
    } catch (err) {
      showError(err.response?.data?.message || t('employeeCommission.configSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const basisLabel = (key) => {
    const map = {
      order_count:    t('employeeCommission.orderCount'),
      order_quantity: t('employeeCommission.orderQuantity'),
      order_value:    t('employeeCommission.orderValue'),
    };
    return map[key] || key;
  };

  const commissionSuffix = form.calculationBasis === 'order_value' ? '%' : 'LKR';

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderLeft}>
            <h2 className={styles.modalTitle}>
              {isEdit
                ? t('employeeCommission.editEmployeeConfig')
                : t('employeeCommission.addEmployeeConfig')}
            </h2>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* Employee select — only shown when adding */}
          {!isEdit && (
            <div className={styles.formGroup} style={{ marginBottom: '1rem' }}>
              <label className={`${styles.formLabel} ${styles.formLabelRequired}`}>
                {t('employeeCommission.selectEmployee')}
              </label>
              <div className={styles.searchWrapper}>
                <Search size={16} className={styles.searchIcon} />
                <input
                  className={styles.searchInput}
                  placeholder={t('employeeCommission.searchEmployee')}
                  value={userSearch}
                  onChange={(e) => handleUserSearch(e.target.value)}
                />
              </div>
              <select
                className={`${styles.formSelect} ${errors.userId ? styles.error : ''}`}
                value={form.userId}
                onChange={(e) => setForm({ ...form, userId: e.target.value })}
                style={{ marginTop: '0.5rem' }}
              >
                <option value="">-- Select --</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} {u.employeeNumber ? `(${u.employeeNumber})` : ''}
                  </option>
                ))}
              </select>
              {errors.userId && (
                <span className={styles.formError}>{errors.userId}</span>
              )}
            </div>
          )}

          {isEdit && (
            <div className={styles.infoBox}>
              <Info size={14} />
              <p>
                Editing commission for: <strong>{existingConfig.employee?.name}</strong>
              </p>
            </div>
          )}

          {/* Calculation basis */}
          <div className={styles.formGroup} style={{ marginBottom: '1rem' }}>
            <label className={`${styles.formLabel} ${styles.formLabelRequired}`}>
              {t('employeeCommission.calculationBasisLabel')}
            </label>
            <select
              className={`${styles.formSelect} ${errors.calculationBasis ? styles.error : ''}`}
              value={form.calculationBasis}
              onChange={(e) => setForm({ ...form, calculationBasis: e.target.value })}
            >
              {BASIS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {basisLabel(opt.value)}
                </option>
              ))}
            </select>
            {errors.calculationBasis && (
              <span className={styles.formError}>{errors.calculationBasis}</span>
            )}
          </div>

          {/* Commission amounts */}
          <div className={styles.formGrid} style={{ marginBottom: '1rem' }}>
            <div className={styles.formGroup}>
              <label className={`${styles.formLabel} ${styles.formLabelRequired}`}>
                {t('employeeCommission.confirmCommissionLabel')} ({commissionSuffix})
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                className={`${styles.formInput} ${errors.confirmCommission ? styles.error : ''}`}
                value={form.confirmCommission}
                onChange={(e) => setForm({ ...form, confirmCommission: e.target.value })}
                placeholder="0.00"
              />
              <span className={styles.formHelp}>
                {t('employeeCommission.confirmCommissionHelp')}
              </span>
              {errors.confirmCommission && (
                <span className={styles.formError}>{errors.confirmCommission}</span>
              )}
            </div>

            <div className={styles.formGroup}>
              <label className={`${styles.formLabel} ${styles.formLabelRequired}`}>
                {t('employeeCommission.createCommissionLabel')} ({commissionSuffix})
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                className={`${styles.formInput} ${errors.createCommission ? styles.error : ''}`}
                value={form.createCommission}
                onChange={(e) => setForm({ ...form, createCommission: e.target.value })}
                placeholder="0.00"
              />
              <span className={styles.formHelp}>
                {t('employeeCommission.createCommissionHelp')}
              </span>
              {errors.createCommission && (
                <span className={styles.formError}>{errors.createCommission}</span>
              )}
            </div>
          </div>

          {/* Time range + Start date side by side */}
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label className={`${styles.formLabel} ${styles.formLabelRequired}`}>
                Commission Period
              </label>
              <select
                className={styles.formSelect}
                value={form.timeRange}
                onChange={(e) => setForm({ ...form, timeRange: e.target.value })}
              >
                {TIME_RANGE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <span className={styles.formHelp}>
                How often commission is calculated
              </span>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                {t('employeeCommission.startDateLabel')}
              </label>
              <input
                type="date"
                className={styles.formInput}
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              />
              <span className={styles.formHelp}>
                {t('employeeCommission.startDateHelp')}
              </span>
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.btnSecondary} onClick={onClose} disabled={saving}>
            {t('employeeCommission.cancel')}
          </button>
          <button className={styles.btnPrimary} onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <div className={styles.spinner} />
                {t('employeeCommission.saving')}
              </>
            ) : (
              t('employeeCommission.save')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeLevelSingleModal;